import React, { useEffect, useState, useRef, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ViewTradesSidebar } from "@/features/accounts/shared/components/view-trades-side-bar";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { useViewTrades } from "@/features/accounts/shared/hooks/use-view-trades";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { useTradesContext } from "@/features/accounts/shared/contexts/trades-context";
import { ConditionalSearchModal } from "@/features/accounts/shared/components/conditional-search-modal";
import { FileAggregateModal } from "@/features/accounts/ocr/components/file-aggregate-modal";
import { CameraAggregateModal } from "@/features/accounts/ocr/components/camera-aggregate-modal";
import { ProcessStatusModal } from "@/features/accounts/ocr/components/process-status-modal";
import { CheckOcrTrades } from "@/features/accounts/ocr/pages/check-ocr-trades";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { BaseRadioButton } from "@/shared/components/radio-button/base-radio-button";
import { UploadCsvExcelModal } from "@/features/accounts/csv-excel/components/upload-csv-excel-modal";
import { TradesOptionModal } from "@/features/accounts/shared/components/trades-option-modal";
import { DownloadTradesModal } from "@/features/accounts/shared/components/download-trades-modal";
import { SyncAccountsStartModal } from "@/features/accounts/shared/components/sync-accounts-start-modal";
import { SyncAccountsModal } from "@/features/accounts/shared/components/sync-accounts-modal";
import { AccountsInfo } from "@/features/accounts/shared/hooks/use-sync-accounts";
import { TradesHistoryModal } from "@/features/accounts/shared/components/trades-history-modal";
import { UpdateAccountNameModal } from "@/features/accounts/shared/components/update-account-name-modal";
import { DeleteAccountModal } from "@/features/accounts/shared/components/delete-account-modal";
import { MemoModal } from "@/features/accounts/shared/components/memo-modal";
import { Checkbox } from "@/shared/components/check-box/check-box"
import { OcrProcessStatus} from "@/shared/config/globals"
import { useInstitutionsContext } from "@/features/institutions/contexts/institutions-context";
import { useTradeJumpContext } from "@/features/trade-jump/contexts/trade-jump-context";
import { UpdateTradeModal } from "@/features/accounts/shared/components/update-trade-modal";
import { useAccountPollingContext } from "@/features/accounts/shared/contexts/account-polling-context";
import { utilities } from "@/shared/utils/utility";


interface ViewTradesProps {
  isOwner: boolean;
  onTapClose: (refresh?: boolean) => void;
  deleteAccountProc: () => void;
  afterAddAccountProc: (addList: AccountsInfo[]) => void;
}

export const ViewTrades = ({ isOwner, onTapClose, deleteAccountProc, afterAddAccountProc }: ViewTradesProps) => {
  const {
    details,
    selectedDetails,
    tradesStartDate,
    tradesEndDate,
    completeFirstFetch,
    setCompleteFirstFetch,
    sortType,
    setSortType,
    activeInvalid,
    setActiveInvalid,
    activeBalance,
    startDateForce,
    endDateForce,
    setForceTradesSetDone,
  } = useTradesContext();
  const {
      setAuthType,
    } = useInstitutionsContext();
  const {
    filterDetails,
    updateDetailValid,
    getTrdaes,
  } = useViewTrades();
  const {
    openHomeModal,
    setOpenHomeModal,
  } = useModalContext();
  const {
    setAccounts,
    selectedAccount,
    setSelectedAccount,
  } = useAccountGroupsContext();
  const {
      tradeJumpMode,
      syncJump,
      jumpDetailSid,
      jumpStartDate,
      jumpEndDate,
      setTradeJumpMode,
  } = useTradeJumpContext();
  const { pollingInfos } = useAccountPollingContext();
  const { getPreviousDay } = utilities();
  const [reloadFlag, setReloadFlag] = useState(0);
  // ajax対応用
  const [topScroll, setTopScroll] = useState(0);
  const [bottomScroll, setBottomScroll] = useState(0);
  const [loading, setLoading] = useState(false);
  // getTrdaes二重実行抑止用
  const fetchingRef = useRef(false);

  const [updateBalanceFlag, setUpdateBalanceFlag] = useState<boolean>(false);
  const [latestBalance, setLatestBalance] = useState<string>(
    selectedAccount?.balance !== undefined && /^-?\d+$/.test(selectedAccount.balance)
      ? selectedAccount.balance
      : ""
  );
  const[refresh, setRefresh] = useState<boolean | undefined>(undefined);

  const jumpTargetRef = useRef<HTMLUListElement | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  // 明細ビューのフォーカストラップ
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    if (!sectionRef.current) return;
    // モーダルが開いている場合はモーダル側に任せる
    if (document.getElementById("modal") || document.getElementById("editOcr")) return;

    const focusableElements = Array.from(
      sectionRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      if (["SECTION", "DIV", "ASIDE"].includes(el.tagName)) return false;
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden";
    });
    if (focusableElements.length === 0) return;

    e.preventDefault();
    const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
    let nextIndex: number;
    if (currentIndex === -1) {
      nextIndex = e.shiftKey ? focusableElements.length - 1 : 0;
    } else if (e.shiftKey) {
      nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
    }
    focusableElements[nextIndex].focus();
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // 再認可用フラグ設定
  useEffect(() => {
    if (!selectedAccount) return;
    if (selectedAccount.industryCode === "01") {
      setAuthType("bank");
    } else if (selectedAccount.industryCode === "03") {
      if (selectedAccount.companyCode === "19659" || selectedAccount.companyCode === "19660") {
        setAuthType("crecaApi");
      } else {
        if (selectedAccount.scrapeType === 2) {
          setAuthType("crecaLookScrape");
        } else if (selectedAccount.scrapeType === 3) {
          setAuthType("crecaLookApi");
        } else {
          setAuthType("");
        }
      }
    } else if (selectedAccount.industryCode === "10") {
      if (selectedAccount.scrapeType === 2) {
        setAuthType("emoneyLookScrape");
      } else if (selectedAccount.scrapeType === 3) {
        setAuthType("emoneyLookApi");
      } else {
        setAuthType("");
      }
    } else if (selectedAccount.industryCode === "20") {
      setAuthType("ecshop");
    } else {
      setAuthType("");
    }
  },[selectedAccount]);

  // 明細取得処理
  const fetchDetails = async (
    type?: "earlier" | "later",
    startDate?: string,
    endDate?: string,
  ): Promise<number | undefined> => {
    if (fetchingRef.current || !selectedAccount) return undefined;
    fetchingRef.current = true;
    setLoading(true);
    let res;
    const fixStartDate = startDateForce  ?? startDate;
    const fixEndDate = endDateForce ?? endDate;
    if (tradeJumpMode) {
      // 明細ジャンプ用明細取得
      res = await getTrdaes(
        selectedAccount.accountGroupId,
        selectedAccount.mlinkAccountId,
        selectedAccount.mlinkId,
        selectedAccount.passbookType,
        selectedAccount.industryCode,
        selectedAccount.companyCode,
        jumpStartDate,
        jumpEndDate
      );
    } else {
      // 通常の明細取得
      res = await getTrdaes(
        selectedAccount.accountGroupId,
        selectedAccount.mlinkAccountId,
        selectedAccount.mlinkId,
        selectedAccount.passbookType,
        selectedAccount.industryCode,
        selectedAccount.companyCode,
        fixStartDate,
        fixEndDate,
        (type === "earlier" ? true : false),
        (type === "later" ? true : false)
      );
      if (updateBalanceFlag) {
        // 明細再取得の場合，取得した一番最初の明細の残高を最新残高としてセット
        if (res && res.length > 0) {
          const firstDetailWithBalance = res.find(detail => detail.balance !== undefined);
          if (firstDetailWithBalance && firstDetailWithBalance.balance != null) {
            setLatestBalance(firstDetailWithBalance.balance.toString());
          }
        }
        setUpdateBalanceFlag(false);
        setRefresh(true)
      }
    }
    if (startDateForce !== undefined || endDateForce !== undefined) {
      setForceTradesSetDone(true);
    }
    setCompleteFirstFetch(true);
    fetchingRef.current = false;
    setLoading(false);
  };

  useEffect(() => {
    if (selectedDetails.length === 0) return;
    if (tradeJumpMode) {
      jumpTargetRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setTradeJumpMode(false);
    }
  }, [selectedDetails]);

  // 上スクロール時処理
  useEffect(() => {
    const topScrollFetchDetails = async () => {
      if (!completeFirstFetch) return;
      const type = sortType === "asc" ? "earlier" : "later";
      const startDate = sortType === "asc" ? undefined : tradesEndDate;
      const endDate = sortType === "asc" ? getPreviousDay(tradesStartDate) : undefined;
      await fetchDetails(type, startDate, endDate);
    };
    topScrollFetchDetails();
  }, [topScroll]);

  // 下スクロール時処理
  useEffect(() => {
    const bottomScrollFetchDetails = async () => {
      if (!completeFirstFetch) return;
      const type = sortType === "asc" ? "later" : "earlier";
      const startDate = sortType === "asc" ? tradesEndDate : undefined;
      const endDate = sortType === "asc" ? undefined : getPreviousDay(tradesStartDate);
      await fetchDetails(type, startDate, endDate);
    };
    bottomScrollFetchDetails();
  }, [bottomScroll]);

  // 初回明細取得&再取得処理
  useEffect(() => {
    if (!selectedAccount) return;
    const fetchItems = () => {
      fetchDetails();
    };
    fetchItems();
  },[selectedAccount, reloadFlag]);

  // ポーリング完了検知: pollingInfosが空になったら明細を再取得
  const prevPollingCountRef = useRef(0);
  useEffect(() => {
    if (prevPollingCountRef.current > 0 && pollingInfos.length === 0) {
      setReloadFlag(prev => prev + 1);
      setUpdateBalanceFlag(true);
      setForceTradesSetDone(false);
    }
    prevPollingCountRef.current = pollingInfos.length;
  }, [pollingInfos.length]);

  // 明細フィルタリング処理
  useEffect(() => {
    if (!selectedAccount) {
      return;
    }
    filterDetails(selectedAccount.passbookType);
  },[details, sortType, activeInvalid, selectedAccount]);

  // ajax
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;

          if (entry.target === topRef.current) {
            setTopScroll(prev => prev + 1); // 上方向
          }

          if (entry.target === bottomRef.current) {
            setBottomScroll(prev => prev + 1); // 下方向
          }
        });
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0,
      }
    );
    if (topRef.current) observer.observe(topRef.current);
    if (bottomRef.current) observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, []);

  const [tradeId, setTradeId] = useState("");
  const [ocrProcessId, setOcrProcessId] = useState("");
  const [memo, setMemo] = useState("");
  const [viewMode, setViewMode] = useState<"check" | "preview">("check");
  const openMemo = (tradeId: string, memo: string) => () => {
    setTradeId(tradeId);
    setMemo(memo);
    setOpenHomeModal("Memo");
  };
  const openImagePreview = (tradeId: string, ocrProcessId: string, mode: "check" | "preview") => () => {
    setViewMode(mode);
    setTradeId(tradeId);
    setOcrProcessId(ocrProcessId);
    setOpenCheckOcr(true);
  }
  const [isExiting, setIsExiting] = useState(false);

  const [openCheckOcr, setOpenCheckOcr] = useState<boolean>(false);

  const onTapCheckOcr = () => {
    if (!selectedAccount) {
      return;
    }
    if (selectedAccount.ocrProcessStatus === OcrProcessStatus.Completed) {
        setOpenCheckOcr(true);
        return;
    }
    else {
      setOpenHomeModal("OcrProcess");
      return;
    }
  };

  if (selectedAccount === undefined) {
    return null;
  }
  return (
    <AnimatePresence>
      <motion.section
        ref={sectionRef}
        key="modal"
        className="list-detail active"
        initial={{ x: "100%" }}
        animate={{ x: isExiting ? "100%" : 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        onAnimationComplete={() => {
          if (isExiting) onTapClose(refresh);
        }}
      >
        <input type="checkbox" id="cheackStatus" className="cheack-status" checked={activeInvalid} readOnly style={{display: 'none'}} />
        <input type="checkbox" id="dspBalance" className="dsp-balance" checked={activeBalance} readOnly style={{display: "none"}} />
        <div className="list-box">
          <header>
            <div className="fc-title">
              <BaseRoundButton
                buttonText="戻る"
                className="ctrl-list"
                onClick={() => {setIsExiting(true);}}
              />
              <h2><span>{selectedAccount.authorizationStatus === 4 ? `【無効】${selectedAccount.passbookName}` : selectedAccount.passbookName}</span></h2>
              {latestBalance !== undefined && (
                (/^-?\d+$/.test(latestBalance)) && (<p>{latestBalance.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</p>)
              )}
            </div>
            <ul className="list-signage">
              <li className="th-hidden">
                <button type="button" onClick={() => setActiveInvalid(false)} tabIndex={activeInvalid ? 0 : -1}>
                  ×&nbsp;閉じる
                </button>
              </li>
              <li className="th-summary">
                <BaseRadioButton
                  id="desc"
                  label=""
                  value="降順"
                  name="sortType"
                  checked={sortType === "desc"}
                  onChange={() => {
                    if (loading) return;
                    setSortType("desc")
                  }}
                  className="rdo-sort-asc"
                  labelFirst={false}
                />
                <strong>取引内容</strong>
                <BaseRadioButton
                  id="asc"
                  label=""
                  value="昇順"
                  name="sortType"
                  checked={sortType === "asc"}
                  onChange={() => {
                    if (loading) return;
                    setSortType("asc")
                  }}
                  className="rdo-sort-des"
                  labelFirst={true}
                />
              </li>
              <li className="th-out">出金</li>
              <li className="th-in">入金</li>
              <li className="th-price">入出金</li>
              <li className="th-balance">残高</li>
            </ul>
          </header>

          <div className="inner-list">
            <div ref={topRef}>
              {loading && "Loading..."}
            </div>
            {selectedDetails.map(((detail, index, arr) => (
              <ul
                key={detail.tradeId}
                ref={detail.tradeId.toLowerCase() === jumpDetailSid?.toLowerCase() ? jumpTargetRef : null}
                data-date={index === 0 || detail.date !== arr[index - 1].date ? detail.date : undefined}
              >
                <li>
                  <dl className={`${detail.invalid ? "list-item invalid" : "list-item"}${detail.tradeId.toLowerCase() === jumpDetailSid?.toLowerCase() ? " jumptrade" : ""}`}>
                    <dt className="list-summary">
                      {(detail.isHeader === 0 && detail.isEcshopTrade === 1 || activeInvalid === false) ? (
                        <></>
                      ) : (
                      <Checkbox
                        id={detail.tradeId}
                        label=""
                        checked={detail.invalid === 0 || detail.invalid === undefined}
                        handleChange={checked => updateDetailValid(detail.tradeId,
                                                                    detail.orderId,
                                                                    checked,
                                                                    selectedAccount.passbookType)}
                        labelClassName="btn-swich"
                      />
                      )}
                      {(detail.isHeader === 1 && detail.isEcshopTrade === 1) ? (
                      <strong>{detail.summary}</strong>
                      ):(
                      <strong>&emsp;{detail.summary}</strong>
                      )}
                      {selectedAccount.passbookType === "101" && (
                        <BaseRoundButton
                            buttonText="OCR"
                            className="ocr open-ocr"
                            onClick={openImagePreview(detail.tradeId, detail.ocrProcessId, "preview")}
                        />
                      )}
                      {detail.isHeader === 0 && (
                        <BaseRoundButton
                          buttonText="メモ"
                          className={"memo" + (detail.memo ? " has-tooltip" : "")}
                          memo={detail.memo}
                          onClick={openMemo(detail.tradeId, detail.memo)}
                          isActive={!syncJump && isOwner}
                        />
                      )}
                      {/* <div className="dsp-memo"></div> */}
                    </dt>
                    <dd className={`list-out ${detail.tradeType === 1 ? "notext" : ""}`}>{detail.tradeType === 1 ? "" : (detail.amount).toLocaleString()}</dd>
                    <dd className={`list-in ${detail.tradeType === 1 ? "" : "notext"}`}>{detail.tradeType === 1 ? (detail.amount).toLocaleString() : ""}</dd>
                    <dd className="list-price">{((detail.tradeType === 1 ? 1 : -1) * detail.amount).toLocaleString()}</dd>
                    <dd className={`list-balance ${detail.balance != null ? "" : "notext"}`}>{detail.balance != null ? detail.balance.toLocaleString() : ""}</dd>
                  </dl>
                </li>
              </ul>
            )))}
            <div ref={bottomRef}>
              {loading && completeFirstFetch && "Loading..."}
            </div>
          </div>
        </div>
  
        <ViewTradesSidebar
          isOwner={isOwner}
          passbookType={selectedAccount.passbookType}
          authorizationStatus={selectedAccount.authorizationStatus}
          onTapCheckOcr={onTapCheckOcr}
          ocrStatus={selectedAccount.ocrProcessStatus}
        />

        {openHomeModal === "Memo" && (
          <MemoModal
            tradeId={tradeId}
            memo={memo}
            passbookType={selectedAccount.passbookType}
            onTapCompleteModal={() => setOpenHomeModal(undefined)}
            onTapCloseModal={() => setOpenHomeModal(undefined)}
          />
        )}

        {openHomeModal === "ConditionalSearch" && (
          <ConditionalSearchModal
            onTapCompleteModal={() => {
              filterDetails(selectedAccount.passbookType);
              setOpenHomeModal(undefined)
            }}
            onTapCloseModal={() => {
              setOpenHomeModal(undefined);
            }}
          />
        )}

        {openHomeModal === "UploadExcel" && (
          <UploadCsvExcelModal
            csvExcelAccountId={selectedAccount.mlinkAccountId}
            passbookName={selectedAccount.passbookName}
            onCompleteModal={() => {
              setOpenHomeModal(undefined);
              // 明細追加後、再取得処理
              setReloadFlag(reloadFlag + 1);
              setUpdateBalanceFlag(true);
            }}
            onCloseModal={() => setOpenHomeModal(undefined)}
          />
        )}

        {openHomeModal === "UploadCsv" && (
          <UploadCsvExcelModal
            csvExcelAccountId={selectedAccount.mlinkAccountId}
            onCompleteModal={() => {
              setOpenHomeModal(undefined);
              // 明細追加後、再取得処理
              setReloadFlag(reloadFlag + 1);
              setUpdateBalanceFlag(true);
            }}
            onCloseModal={() => setOpenHomeModal(undefined)}
          />
        )}

        {openHomeModal === "AddOcrTrades" && (
          <FileAggregateModal
            onTapCloseModal={() => setOpenHomeModal(undefined)}
          />
        )}

        {openHomeModal === "Camera" && (
          <CameraAggregateModal
            onTapCloseModal={() => setOpenHomeModal(undefined)}
          />
        )}

        {openHomeModal === "OcrProcess" && (
          <ProcessStatusModal
            userMessage={selectedAccount.ocrProcessStatus === OcrProcessStatus.Processing ? "OCR処理中です" : ""}
            onTapCloseModal={() => {setOpenHomeModal(undefined);}}
          />
        )}

        {openHomeModal === "Option" && (
          <TradesOptionModal
            isOwner={isOwner}
            maintenance={selectedAccount.isUnderMaintenance}
            passbookType={selectedAccount.passbookType}
            scrapeType={selectedAccount.scrapeType}
            onTapCloseModal={() => setOpenHomeModal(undefined)}
          />
        )}

        {openHomeModal === "Download" && (
          <DownloadTradesModal
            downloadAllAtOnce={false}
            onTapCloseModal={() => setOpenHomeModal(undefined)}
            onTapCompleteModal={() => setOpenHomeModal(undefined)}
          />
        )}

        {(openHomeModal === "SyncAccountsStart" || openHomeModal === "SyncOtp") && (
            <SyncAccountsStartModal
            onTapCloseModal={() => {
              setOpenHomeModal(undefined);
            }}
          />
          )}

        {openHomeModal === "SyncAccounts" && (
          <SyncAccountsModal
            passbookType={selectedAccount.passbookType}
            accountGroupId={selectedAccount.accountGroupId}
            mlinkAccountId={selectedAccount.mlinkAccountId}
            mlinkId={selectedAccount.mlinkId}
            scrapeType={selectedAccount.scrapeType}
            onTapCompleteModal={(addList) => {
              afterAddAccountProc(addList);
            }}
            onTapCloseModal={() => setOpenHomeModal(undefined)}
          />
        )}

        {openHomeModal === "History" && (
          <TradesHistoryModal
            accountGroupId={selectedAccount.accountGroupId}
            mlinkAccountId={selectedAccount.mlinkAccountId}
            mlinkId={selectedAccount.mlinkId}
            passbookType={selectedAccount.passbookType}
            onTapCloseModal={() => setOpenHomeModal(undefined)}
          />
        )}

        {openHomeModal === "EditName" && (
          <UpdateAccountNameModal
            accountGroupId={selectedAccount.accountGroupId}
            mlinkAccountId={selectedAccount.mlinkAccountId}
            mlinkId={selectedAccount.mlinkId}
            passbookType={selectedAccount.passbookType}
            passbookName={selectedAccount.passbookName}
            onTapCloseModal={() => setOpenHomeModal(undefined)}
            onTapCompleteModal={(name) => {
              setOpenHomeModal(undefined);
              setSelectedAccount(prev => {
                if (prev === undefined) return undefined;
                return {
                  ...prev,
                  passbookName: name,
                };
              });
              setRefresh(true);
            }}
          />
        )}

        {openHomeModal === "Delete" && (
          <DeleteAccountModal
            accountGroupId={selectedAccount.accountGroupId}
            mlinkAccountId={selectedAccount.mlinkAccountId}
            mlinkId={selectedAccount.mlinkId}
            passbookType={selectedAccount.passbookType}
            onTapCloseModal={() => setOpenHomeModal(undefined)}
            onTapCompleteModal={() => {
              setOpenHomeModal(undefined);
              deleteAccountProc();
            }}
          />
        )}

        <UpdateTradeModal
            accountGroupId={selectedAccount.accountGroupId}
            mlinkAccountId={selectedAccount.mlinkAccountId}
            mlinkId={selectedAccount.mlinkId}
            passbookType={selectedAccount.passbookType}
            scrapeType={selectedAccount.scrapeType}
            otpYn={selectedAccount.otpYn}
            onTapCompleteModal={() => {
              setOpenHomeModal(undefined);
              setReloadFlag(reloadFlag + 1);
              setUpdateBalanceFlag(true);
            }}
            onTapCloseModal={() => setOpenHomeModal(undefined)}
          />

      </motion.section>
      {openCheckOcr && (
        <CheckOcrTrades
          onTapCloseModal={() => {
            setOpenCheckOcr(false)
            setViewMode("check")
          }}
          onCompleteModal={() => {
            setOpenCheckOcr(false)
            setViewMode("check")
            setReloadFlag(reloadFlag + 1);
            setUpdateBalanceFlag(true);
            // OCR完了後、ocrProcessIdを空文字，ocrProcessStatusを0に更新
            setAccounts(prev => prev.map(acc =>
              acc.mlinkAccountId === selectedAccount.mlinkAccountId && acc.passbookType === selectedAccount.passbookType
                ? { ...acc, ocrProcessId: "", ocrProcessStatus: 0 }
                : acc
            ));
            if (selectedAccount && selectedAccount.mlinkAccountId === selectedAccount.mlinkAccountId && selectedAccount.passbookType === selectedAccount.passbookType) {
              setSelectedAccount({ ...selectedAccount, ocrProcessId: "", ocrProcessStatus: 0 });
            }
          }}
          viewMode={viewMode}
          tradeId={viewMode === "preview" ? tradeId : undefined}
          processId={viewMode === "preview" ? ocrProcessId : undefined}
        />
      )}
    </AnimatePresence>
  );
};
